import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';
import { Loader } from '@parkdna/ui/Backdrop';
import { useTheme } from '@parkdna/ui/theme';
import { useAuth } from 'hooks/auth/useAuth';
import { startTransition, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AvatarShimmered = styled(Avatar)({
  '@keyframes shimmer': {
    '100%': {
      maskPosition: 'left',
    },
  },
  mask: 'linear-gradient(-45deg, #000 30%, #0005, #000 60%) right/400% 100%',
});

const Topbar = () => {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [shimmer, setShimmer] = useState(false);

  const [theme, toggleTheme] = useTheme();
  const { signOut, user, orgName, roles, isLoading } = useAuth();

  const handleAvatarClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(open ? null : event.currentTarget);
    },
    [open]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSignOut = async () => {
    handleClose();
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const handleUserSettingsClick = () => {
    handleClose();
    navigate('/profile');
  };

  useEffect(() => {
    document.body.style.backgroundColor = theme.palette.background.default;
  }, [theme]);

  const handleToggleThemeClicked = () => {
    startTransition(() => {
      toggleTheme();
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
      }}
    >
      <IconButton
        aria-label={
          theme.palette.mode === 'dark'
            ? 'Switch to Light Mode'
            : 'Switch to Dark Mode'
        }
        sx={{ alignSelf: 'end' }}
        onClick={handleToggleThemeClicked}
      >
        {theme.palette.mode === 'dark' ? (
          <DarkModeOutlinedIcon />
        ) : (
          <LightModeOutlinedIcon />
        )}
      </IconButton>
      <IconButton
        aria-label="Open Account Menu"
        onClick={handleAvatarClick}
        sx={{
          padding: 0,
          marginRight: theme.spacing(1.5),
          alignSelf: 'end',
        }}
      >
        <AvatarShimmered
          src={user?.picture}
          alt="Profile picture"
          onMouseEnter={() => setShimmer(true)}
          onAnimationEnd={() => setShimmer(false)}
          sx={{
            ...(shimmer && { animation: 'shimmer 1s linear' }),
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              overflow: 'visible',
              mt: 1.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: (theme) => theme.palette.background.paper,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            <Box
              key="user-info"
              sx={{
                padding: 2,
                paddingTop: 0.5,
                display: 'flex',
                alignItems: 'start',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1">{`${user?.name}(${orgName})`}</Typography>
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  fontWeight: 400,
                }}
              >
                {user?.email}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.25 }}>
                {roles?.map((role) => (
                  <Chip
                    variant="outlined"
                    key={role}
                    sx={(theme) => ({
                      background: `radial-gradient(circle at top right, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      color: theme.palette.primary.contrastText,
                    })}
                    size="small"
                    label={role}
                  />
                ))}
              </Box>
            </Box>
            <Divider sx={{ mb: 1 }} key="divider-1" />
            <MenuItem key="personal-settings" onClick={handleUserSettingsClick}>
              <ListItemIcon>
                <PersonOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="Personal settings" />
            </MenuItem>
            <Divider key="divider-2" />
            <MenuItem key="sign-out" onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Sign out" />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default Topbar;
